import { useAuth } from "@clerk/react";
import { Link, useParams } from "@tanstack/react-router";
import { Button } from "@ndd/ui";
import { ArrowLeft, Sparkles } from "lucide-react";
import type { ReactElement } from "react";
import { SoapDraftReview } from "@/components/appointments/appointment-draft-editor";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";

/**
 * The SOAP draft of an Appointmentless Clinical Session: the same review and
 * editing surface as the Appointment workspace, minus approval (there is no
 * Healthie appointment to send it to). Copy is the way into Healthie.
 */
export function SessionDraftPage(): ReactElement {
  const { getToken } = useAuth();
  const { sessionId } = useParams({ strict: false }) as { sessionId: string };

  return (
    <>
      <BreadcrumbSetter
        segments={[
          { label: "Appointments", href: "/appointments" },
          { label: "SOAP draft" },
        ]}
      />
      <div className="flex h-full min-h-0 justify-center overflow-hidden">
        <div className="flex min-h-0 w-full max-w-[720px] flex-col px-5 py-5 sm:px-6 sm:py-8">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="-ml-2 self-start text-body text-muted-foreground hover:text-foreground"
          >
            <Link to="/appointments">
              <ArrowLeft aria-hidden="true" /> Back to Appointments
            </Link>
          </Button>
          <p className="mt-3 flex items-center gap-1.5 font-sans text-caption font-extrabold uppercase tracking-[0.08em] text-subtle">
            Sessions
            <span aria-hidden="true" className="text-sand-300">
              /
            </span>
            SOAP draft
          </p>
          <header className="mt-3 shrink-0">
            <h1 className="flex items-center gap-2 font-sans text-display font-bold leading-tight tracking-[-0.015em] text-foreground">
              <Sparkles aria-hidden="true" className="size-5 text-ai" />
              Enhanced SOAP draft
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This session isn&apos;t linked to a Healthie appointment. Review and edit
              the draft here, then copy it into Healthie.
            </p>
          </header>
          <div className="mt-[22px] flex min-h-0 flex-1 flex-col">
            <SoapDraftReview
              key={sessionId}
              target={{ kind: "session", id: sessionId }}
              ready
              getToken={getToken}
            />
          </div>
        </div>
      </div>
    </>
  );
}
